import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { useProjectOverviewQuery } from "@/api/hooks";
import { usePackFileQuery, useSavePackFileMutation } from "@/api/packFiles";
import {
  useFreezeCandidateMutation,
  usePreviewCandidatesQuery,
} from "@/api/preview";
import {
  useCreatePreviewSimRunMutation,
  usePreviewSimRunQuery,
} from "@/api/previewSim";
import { useCapability } from "@/auth/SessionContext";
import {
  AB_FAILURE_MESSAGES,
  AbError,
  FEATURES_PATH,
  newestCurrentCandidateId,
  runFeatureAb,
  runOptionAb,
  type AbDeps,
  type AbFeatureValue,
} from "@/lib/labAb";
import {
  RUN_OPTIONS,
  featureValueLabel,
  type FeatureEntry,
  type LabFlags,
} from "@/lib/labServices";
import { queryKeys } from "@/api/hooks";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Field, Select } from "@/ui/Input";

const MODE_VALUES = ["off", "shadow", "on"] as const;

/** The values a feature can move to: a flip for a boolean, the other modes otherwise. */
export function alternativesFor(value: AbFeatureValue): AbFeatureValue[] {
  return typeof value === "boolean"
    ? [!value]
    : MODE_VALUES.filter((mode) => mode !== value);
}

/**
 * "A/B": runs the same simulated patient twice. "A/B a pack feature" uses this
 * testing version and a copy that differs in exactly one pack feature. "A/B a
 * run option" uses the SAME testing version twice, differing only in one run
 * option (`labFlags`). The simulator has no seed, so the two runs share the
 * persona, language and turn budget, not the exact words.
 */
export function LabAbDialog({
  projectId,
  previewRunRef,
  features,
  serviceFlags,
  onClose,
  onDone,
}: {
  projectId: string;
  previewRunRef: string;
  features: readonly FeatureEntry[];
  serviceFlags: readonly FeatureEntry[];
  onClose: () => void;
  onDone: (baseRunRef: string, variantRunRef: string) => void;
}) {
  const create = useCapability("preview.create");
  const queryClient = useQueryClient();
  const sim = usePreviewSimRunQuery(projectId, previewRunRef, {
    pollIntervalMs: false,
  });
  const overview = useProjectOverviewQuery(projectId);
  const candidates = usePreviewCandidatesQuery(projectId);
  const file = usePackFileQuery(projectId, FEATURES_PATH);
  const save = useSavePackFileMutation(projectId);
  const freeze = useFreezeCandidateMutation(projectId);
  const startSim = useCreatePreviewSimRunMutation(projectId);

  const [mode, setMode] = useState<"pack" | "option">(
    features.length > 0 ? "pack" : "option",
  );
  // The source run's run options ride on both new runs; only the A/B one differs.
  const inherited: LabFlags = Object.fromEntries(
    serviceFlags
      .filter((flag) => flag.source === "run_option")
      .map((flag) => [flag.name, String(flag.value)]),
  );
  const [optionName, setOptionName] = useState<string>(RUN_OPTIONS[0].name);
  const option =
    RUN_OPTIONS.find((o) => o.name === optionName) ?? RUN_OPTIONS[0];
  const baseOptionValue = String(
    serviceFlags.find((flag) => flag.name === option.name)?.value ?? "off",
  );
  const optionAlternatives = option.values.filter(
    (v) => v.value !== baseOptionValue,
  );
  const [chosenOption, setChosenOption] = useState<string | null>(null);
  const optionValue =
    optionAlternatives.find((v) => v.value === chosenOption)?.value ??
    optionAlternatives[0]?.value;
  const [name, setName] = useState(features[0]?.name ?? "");
  const selected = features.find((feature) => feature.name === name);
  const options = selected === undefined ? [] : alternativesFor(selected.value);
  const [chosen, setChosen] = useState<AbFeatureValue | null>(null);
  const value =
    chosen !== null && options.includes(chosen) ? chosen : options[0];
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const attempt = useRef(crypto.randomUUID());

  const simStatus = sim.data ?? null;
  const revision = overview.data?.project.previewTargetRevision;
  const blocker = !create.allowed
    ? `${create.reason} This needs the "preview.create" permission.`
    : sim.isPending
      ? "Loading the run…"
      : simStatus === null
        ? "A/B needs a simulated-patient run; this run was typed in by hand."
        : revision === undefined
          ? "Loading the project…"
          : mode === "pack" && (selected === undefined || value === undefined)
            ? "Choose a feature."
            : mode === "option" && optionValue === undefined
              ? "Choose a run option."
              : null;

  async function start() {
    if (
      blocker !== null ||
      simStatus === null ||
      revision === undefined ||
      (mode === "pack" && (selected === undefined || value === undefined)) ||
      (mode === "option" && optionValue === undefined)
    ) {
      return;
    }
    setBusy(true);
    setFailure(null);
    const runOn = async (candidateId: string, labFlags?: LabFlags) =>
      (
        await startSim.mutateAsync({
          candidateId,
          personaId: simStatus.personaId,
          language: simStatus.language,
          maxTurns: simStatus.maxTurns,
          ...(labFlags === undefined || Object.keys(labFlags).length === 0
            ? {}
            : { labFlags }),
          expectedPreviewTargetRevision: revision,
          idempotencyKey: crypto.randomUUID(),
        })
      ).previewRunRef;
    try {
      const packDeps: AbDeps = {
        async readFeaturesFile() {
          const read = (await file.refetch({ throwOnError: true })).data;
          return {
            content: read?.content ?? "",
            revision: read?.revision ?? 0,
          };
        },
        async writeFeaturesFile(content, expectedRevision) {
          const saved = await save.mutateAsync({
            path: FEATURES_PATH,
            content,
            expectedRevision,
          });
          return { revision: saved.revision };
        },
        currentCandidateId: () =>
          newestCurrentCandidateId(candidates.data?.items ?? []),
        async freeze() {
          return (
            await freeze.mutateAsync({ idempotencyKey: crypto.randomUUID() })
          ).candidateId;
        },
        startRun: runOn,
      };
      const done =
        mode === "pack" && selected !== undefined && value !== undefined
          ? await runFeatureAb(
              packDeps,
              { name: selected.name, value },
              inherited,
            )
          : await runOptionAb(
              packDeps,
              {
                name: option.name,
                baseValue: baseOptionValue,
                variantValue: optionValue ?? baseOptionValue,
              },
              inherited,
            );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.previewCandidates(projectId),
      });
      attempt.current = crypto.randomUUID();
      onDone(done.baseRunRef, done.variantRunRef);
      onClose();
    } catch (error) {
      setFailure(
        error instanceof AbError
          ? AB_FAILURE_MESSAGES[error.kind]
          : "The A/B could not be started.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={mode === "pack" ? "A/B a pack feature" : "A/B a run option"}
        description={
          mode === "pack"
            ? "Runs the same simulated patient on this testing version and on a copy with one pack feature changed, then opens both side by side. The simulator has no seed, so the runs match in persona, language and turn budget, not in exact words."
            : "Runs the same simulated patient twice on this testing version, changing only one run option, then opens both side by side. The simulator has no seed, so the runs match in persona, language and turn budget, not in exact words."
        }
        busy={busy}
        footer={
          <>
            <Button variant="outline" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={blocker !== null || busy}
              title={blocker ?? undefined}
              onClick={() => void start()}
            >
              {busy ? "Running the A/B…" : "Run A/B"}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div role="group" aria-label="A/B mode" className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={mode === "pack" ? "primary" : "outline"}
              aria-pressed={mode === "pack"}
              disabled={busy || features.length === 0}
              onClick={() => setMode("pack")}
            >
              A/B a pack feature
            </Button>
            <Button
              type="button"
              size="sm"
              variant={mode === "option" ? "primary" : "outline"}
              aria-pressed={mode === "option"}
              disabled={busy}
              onClick={() => setMode("option")}
            >
              A/B a run option
            </Button>
          </div>
          {mode === "pack" ? (
            <>
              <Field label="Feature">
                {({ inputId }) => (
                  <Select
                    id={inputId}
                    value={name}
                    disabled={busy}
                    onChange={(event) => {
                      setName(event.target.value);
                      setChosen(null);
                    }}
                  >
                    {features.map((feature) => (
                      <option key={feature.name} value={feature.name}>
                        {feature.name} (now {featureValueLabel(feature.value)})
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Variant value">
                {({ inputId }) => (
                  <Select
                    id={inputId}
                    value={String(value)}
                    disabled={busy || options.length < 2}
                    onChange={(event) =>
                      setChosen(
                        options.find((o) => String(o) === event.target.value) ??
                          null,
                      )
                    }
                  >
                    {options.map((option) => (
                      <option key={String(option)} value={String(option)}>
                        {featureValueLabel(option)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </>
          ) : (
            <>
              <Field label="Run option">
                {({ inputId }) => (
                  <Select
                    id={inputId}
                    value={optionName}
                    disabled={busy}
                    onChange={(event) => {
                      setOptionName(event.target.value);
                      setChosenOption(null);
                    }}
                  >
                    {RUN_OPTIONS.map((o) => (
                      <option key={o.name} value={o.name}>
                        {o.name} (this run: {baseOptionValue})
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Variant value">
                {({ inputId }) => (
                  <Select
                    id={inputId}
                    value={optionValue ?? ""}
                    disabled={busy || optionAlternatives.length < 2}
                    onChange={(event) => setChosenOption(event.target.value)}
                  >
                    {optionAlternatives.map((v) => (
                      <option key={v.value} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </>
          )}
          {blocker !== null ? (
            <p className="text-sm text-muted-foreground">{blocker}</p>
          ) : null}
          {failure !== null ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
            >
              {failure}
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
